import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Alert — уведомление-врезка (`.alert` макета) вариантов danger/info. Фон/текст из токенов
 * (danger — статусный цвет ошибки), граница — токены border-alert-danger / border-alert-info.
 * Роль role="alert" — содержимое объявляется AT.
 */
const alertVariants = cva('flex items-start gap-4 rounded-12 border px-5 py-4', {
  variants: {
    variant: {
      danger: 'border-alert-danger bg-red-bg',
      info: 'border-alert-info bg-blue-bg',
    },
  },
  defaultVariants: { variant: 'danger' },
})

const titleColor = {
  danger: 'text-red',
  info: 'text-accent-txt',
} as const

export interface AlertProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof alertVariants> {
  title?: ReactNode
  icon?: ReactNode
  actions?: ReactNode
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant = 'danger', title, icon, actions, children, ...props }, ref) => (
    <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
      {icon ? <span className={cn('mt-0.5 shrink-0', titleColor[variant!])}>{icon}</span> : null}
      <div className="min-w-0 flex-1">
        {title ? (
          <div className={cn('text-15 font-semibold', titleColor[variant!])}>{title}</div>
        ) : null}
        {children ? <div className={cn(title && 'mt-1', 'text-txt')}>{children}</div> : null}
      </div>
      {actions ? <div className="shrink-0">{actions}</div> : null}
    </div>
  ),
)
Alert.displayName = 'Alert'

export { alertVariants }
